import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { FlattenSimpleInterpolation } from "styled-components/macro";
import { Close, ReportGmailerrorred } from "@mui/icons-material";
import { NotificationCard } from "./NotificationCard";
import { useServiceStatus } from "./useServiceStatus";
import { useIsAdmin, useInterval } from "utils/customHooks/genericHooks";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import ReactJson from "react-json-view";
import { IconButton } from "@mui/material";
import { useState } from "react";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useMount } from "react-use";
import { useTraceToHoneycomb } from "tracer";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";

/** Indicates that Raven is experiencing a service outage */
const ServiceOutageIndicator = ({
  customCss,
}: {
  customCss?: string | FlattenSimpleInterpolation;
}) => {
  const [servicesStatus] = useServiceStatus();
  const { midgard } = servicesStatus;
  const isAdminUser = useIsAdmin();
  const { flightServiceOutage, flightDegradedServiceBannerEnabled } =
    useFlagsWithDefaults();
  const [dismissed, setDismissed] = useState(false);
  const { translate } = useFlightTranslation();
  const showDegradedServiceBanner =
    midgard.breakerOpen &&
    midgard.degraded &&
    flightDegradedServiceBannerEnabled &&
    !dismissed;

  return flightServiceOutage ? (
    <NotificationCard
      message={
        <>
          <span style={{ fontWeight: "bold" }}>
            {translate("Temporary_Outage")}:{" "}
          </span>{" "}
          {translate("TEMPORARY_OUTAGE_MESSAGE")}
        </>
      }
      icon={<ReportGmailerrorred style={{ width: 30, height: 30 }} />}
      variant={"error"}
      customCss={customCss}
    />
  ) : // Degraded Service banner
  showDegradedServiceBanner ? (
    <NotificationCard
      message={
        <>
          <TraceDegradedServiceBannerWasDisplayed />
          <span style={{ fontWeight: "bold" }}>
            {translate("Degraded_Service")}:{" "}
          </span>{" "}
          {translate("DEGRADED_SERVICE_MESSAGE")}
        </>
      }
      icon={
        <HtmlTooltip
          title={
            isAdminUser || process.env.NODE_ENV === "development" ? (
              <ErrorJson src={servicesStatus} />
            ) : (
              ""
            )
          }
          leaveDelay={500}
          PopperProps={{ style: { pointerEvents: "all" } }}
        >
          <ReportGmailerrorred style={{ width: 30, height: 30 }} />
        </HtmlTooltip>
      }
      variant={"warning"}
      customCss={customCss}
      data-component-name={"Degraded Service Indicator"}
      ActionButton={
        <IconButton
          style={{ marginLeft: "auto" }}
          onClick={() => {
            setDismissed(true);
          }}
        >
          <Close />
        </IconButton>
      }
    />
  ) : // Known Error banner (no breaker)
  !midgard.breakerOpen &&
    midgard.degraded &&
    midgard.latestError &&
    flightDegradedServiceBannerEnabled &&
    !dismissed ? (
    <NotificationCard
      message={
        <>
          <span style={{ fontWeight: "bold" }}>
            {translate("Server_Error")}:{" "}
          </span>{" "}
          {translate("SERVER_ERROR_MESSAGE")}
        </>
      }
      icon={
        <HtmlTooltip
          title={
            isAdminUser || process.env.NODE_ENV === "development" ? (
              <ErrorJson
                src={{
                  ...servicesStatus,
                  suggestion:
                    "use the flag flight-force-reload-app-once once the error is resolved",
                }}
              />
            ) : (
              ""
            )
          }
          leaveDelay={500}
          PopperProps={{ style: { pointerEvents: "all" } }}
        >
          <ReportGmailerrorred style={{ width: 30, height: 30 }} />
        </HtmlTooltip>
      }
      variant={"warning"}
      customCss={customCss}
      data-component-name={"Server Error Indicator"}
      ActionButton={
        <IconButton
          onClick={() => {
            setDismissed(true);
          }}
        >
          <Close />
        </IconButton>
      }
    />
  ) : null;
};
export default ServiceOutageIndicator;

function ErrorJson({ src }: { src: Record<string, unknown> }) {
  return (
    <ReactJson
      src={src}
      name={false}
      displayDataTypes={false}
      displayObjectSize={false}
      quotesOnKeys={false}
    />
  );
}
function TraceDegradedServiceBannerWasDisplayed() {
  const isStorybook = useIsStorybook();
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: isStorybook ? 0 : 1,
  });
  const { user } = useAuth0();
  const ckey = useCustomerKey();
  useMount(() => {
    // trace that the banner became visible
    traceToHoneycomb({
      dataToTrace: {
        name: "Flight Degraded Service Banner Displayed",
        email: String(user?.email),
        ckey,
        url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
    });
  });
  useInterval({
    callback: () => {
      traceToHoneycomb({
        dataToTrace: {
          name: "Flight Degraded Service Banner Displayed",
          email: String(user?.email),
          ckey,
          latestError: "MidgardStatusChecker - breaker still open",
          url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      });
    },
    immediate: false,
    delay: ONE_MINUTE_OF_MS,
  });

  return null;
}
